// One circle or event: rename, kind or schedule, check in, members and roles, approximate sharing and SOS reach,
// supervision (guardians), places link, invite links/QR codes (supervised too), leave or delete.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth, useMe } from "../auth/AuthContext";
import {
  expiryText,
  formatInviteCode,
  fullText,
  isFull,
  KIND_LABELS,
  kindSwitchText,
  memberActions,
  memberCountText,
  sortMembers,
  usesText,
} from "../circles/circles";
import { forgetCircle } from "../circles/choice";
import { BubbleForm } from "../components/BubbleForm";
import { CheckInCard } from "../components/CheckInCard";
import { EventForm } from "../components/EventForm";
import { MemberAvatar } from "../components/MemberAvatar";
import { AskForm, askError, MySupervisionCard, RequestState } from "../components/MySupervision";
import { QrCode } from "../components/QrCode";
import {
  SupervisionCard,
  SupervisionFields,
  useMembersFollowSupervisions,
  useSupervisions,
} from "../components/SupervisionPanel";
import {
  countdownText,
  deletionText,
  draftFromEvent,
  draftProblem,
  eventStatus,
  eventUpdateBody,
  localTimeZone,
  scheduleText,
  STATUS_LABELS,
  type EventDraft,
} from "../events/events";
import { approximateText, bubblesFor } from "../sharing/bubbles";
import { BUBBLES_KEY, useSharingBubbles } from "../sharing/useSharingBubbles";
import {
  circleControls,
  draftReady,
  eligibleGuardians,
  initialDraft,
  inviteCreateBody,
  involves,
  PAUSE_CHOICES,
  pauseChoiceText,
  type PauseChoice,
  type SupervisionDraft,
} from "../supervision/supervision";
import {
  actionMode,
  bubbleParams,
  isPending,
  SUPERVISION_POLL_MS,
  supervisionPoll,
  myRequestText,
  noticeText,
  recentRequests,
} from "../supervision/member";
import { useAsk, useMyRequests } from "../supervision/useMyRequests";
import { useNow } from "../useNow";
import { useUnits } from "../useUnits";

type Circle = Schemas["Circle"];
type Member = Schemas["Member"];
type Invite = Schemas["Invite"];
type CircleEvent = Schemas["CircleEvent"];
type SupervisedMember = Schemas["SupervisedMember"];

const PointMap = lazy(() => import("../map/PointMap"));

const NAME_MAX = 80;
/** Picks up invite uses this often. */
const INVITES_POLL_MS = 60_000;
/** Refreshes invite expiry labels (and drops expired invites) this often. */
const TICK_MS = 30_000;
const COPIED_MS = 2_000;
const LAST_ADMIN = "A circle needs an admin. Make someone else an admin first, or delete the circle.";

const TTL_OPTIONS = [
  { hours: 1, label: "1 hour" },
  { hours: 24, label: "1 day" },
  { hours: 48, label: "2 days" },
  { hours: 168, label: "7 days" },
] as const;
const DEFAULT_TTL_HOURS = 48;
const MAX_USES_LIMIT = 50;

/** Overrides shared by circle mutations. */
function mutationError(error: unknown): string {
  if (error instanceof ApiError && error.code === "last_admin") return LAST_ADMIN;
  if (error instanceof ApiError && error.code === "invite_limit")
    return "This circle has too many active invites. Revoke one first.";
  if (error instanceof ApiError && error.code === "event_ended") return "The event has ended, so nobody can join.";
  if (error instanceof ApiError && error.code === "event_circle")
    return "An event can't become a family or friends circle.";
  if (error instanceof ApiError && error.code === "supervised_member")
    return "A supervised member can't be made an admin. End their supervision first.";
  if (error instanceof ApiError && error.code === "supervised_members")
    return "Some members are supervised with controls the other kind of circle doesn't have. End those supervisions first.";
  if (error instanceof ApiError && error.code === "supervised")
    return "Your supervision in this circle asks a guardian first.";
  return errorMessage(error, {
    403: "Only admins can do that.",
    404: "That's no longer part of this circle.",
  });
}

/** " Your guardians are told." for a notify-mode action, appended to its confirmation. */
const notice = (mode: Schemas["SupervisionMode"]) => {
  const text = noticeText(mode);
  return text ? ` ${text}` : "";
};

export function CirclePage() {
  const circleId = useParams().circleId ?? "";
  // A fresh subtree per circle: no form, QR or pending-mutation UI state carries over.
  return <CircleView key={circleId} circleId={circleId} />;
}

function CircleView({ circleId }: { circleId: string }) {
  const { session } = useAuth();
  const circle = useQuery({
    queryKey: ["circle", circleId],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}", { params: { path: { circleId } } })),
    // Guardians may revise, loosen or end the caller's supervision meanwhile.
    refetchInterval: (query) => supervisionPoll(query.state.data),
  });
  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } })),
    enabled: circle.isSuccess,
    // A guardian may pause or resume the caller here, or a timed pause run out.
    refetchInterval: circle.data?.supervision ? SUPERVISION_POLL_MS : false,
  });
  const bubbles = useSharingBubbles(circle.data?.supervision ? SUPERVISION_POLL_MS : false);
  const nowMs = useNow(TICK_MS);
  const selfId = session?.userId ?? "";
  const supervisions = useSupervisions(circleId, circle.data?.role, members.data, selfId);
  useMembersFollowSupervisions(circleId, supervisions.data);
  // The member a row's "Supervise" opened the offer form for.
  const [superviseFor, setSuperviseFor] = useState<string | null>(null);

  if (!session) return null;
  if (circle.isPending) return <p className="muted">Loading the circle…</p>;
  if (circle.error) {
    return (
      <div className="stack">
        <p className="error">
          {errorMessage(circle.error, { 404: "This circle doesn't exist, or you're no longer a member." })}
        </p>
        <p>
          <Link to="/circles">Your circles</Link>
        </p>
      </div>
    );
  }

  const isAdmin = circle.data.role === "admin";
  const event = circle.data.event;
  const status = event ? eventStatus(event, nowMs) : null;
  return (
    <div className="stack circle-page">
      <p>
        <Link to="/circles">← Circles</Link>
      </p>
      <CircleHeader circle={circle.data} />
      <p className="muted circle-summary">
        <span className={`kind-badge ${circle.data.kind}`}>{KIND_LABELS[circle.data.kind]}</span>
        {status && <span className={`event-badge ${status}`}>{STATUS_LABELS[status]}</span>}
        {memberCountText(circle.data)}
        {isFull(circle.data) && status !== "ended" && " · Full"}
      </p>
      <p>
        <Link to={`/chats?circle=${encodeURIComponent(circleId)}`} className="circle-chat-link">
          Chat
        </Link>
      </p>
      {event && <EventCard circle={circle.data} event={event} nowMs={nowMs} />}
      {/* Outside the event nobody's location is shared, so check-ins are refused (403 event_not_live). */}
      {members.data && status !== "upcoming" && status !== "ended" && (
        <CheckInCard
          circle={circle.data}
          paused={members.data.find((member) => member.user_id === session.userId)?.sharing === "paused"}
          approximate={bubblesFor(bubbles.bubbles, circleId, nowMs).shown !== null}
        />
      )}
      <MySupervisionCard
        circle={circle.data}
        selfId={session.userId}
        selfName={members.data?.find((member) => member.user_id === session.userId)?.name ?? ""}
      />
      <section className="card stack">
        <h2>Members</h2>
        {members.isPending && <p className="muted">Loading members…</p>}
        {members.error && <p className="error">{errorMessage(members.error)}</p>}
        {members.data && (
          <MemberList
            circle={circle.data}
            members={members.data}
            selfId={session.userId}
            bubbles={bubbles}
            nowMs={nowMs}
            supervisions={supervisions.data ?? []}
            onSupervise={setSuperviseFor}
          />
        )}
        {supervisions.error && <p className="error">{errorMessage(supervisions.error)}</p>}
      </section>
      {members.data && supervisions.data && (
        <SupervisionCard
          circle={circle.data}
          members={members.data}
          selfId={session.userId}
          supervisions={supervisions.data}
          superviseFor={superviseFor}
          onCloseSupervise={() => setSuperviseFor(null)}
        />
      )}
      <section className="card stack">
        <h2>Places</h2>
        <p className="muted">Places like home, school or work, for arrival and departure alerts.</p>
        <p>
          <Link to={`/circles/${circleId}/places`}>Manage places</Link>
        </p>
        <p>
          <Link to={`/circles/${circleId}/notifications`}>Alerts</Link>
        </p>
      </section>
      {isAdmin && circle.data.kind !== "event" && <KindCard circle={circle.data} />}
      {isAdmin && (
        <InvitesCard
          circle={circle.data}
          ended={status === "ended"}
          members={members.data ?? []}
          selfId={session.userId}
        />
      )}
      {isAdmin && <DeleteCircleCard circle={circle.data} />}
    </div>
  );
}

function CircleHeader({ circle }: { circle: Circle }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(circle.name);
  // Cache writes use the response's circle; UI callbacks (passed to mutate) only run while mounted.
  const rename = useMutation({
    mutationFn: async (vars: { circleId: string; name: string }) =>
      unwrap(
        await api.PATCH("/circles/{circleId}", {
          params: { path: { circleId: vars.circleId } },
          body: { name: vars.name },
        }),
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["circle", updated.id], updated);
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) rename.mutate({ circleId: circle.id, name: trimmed }, { onSuccess: () => setEditing(false) });
  }

  if (!editing) {
    return (
      <div className="circle-header">
        <h1>{circle.name}</h1>
        {circle.role === "admin" && (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setName(circle.name);
              rename.reset();
              setEditing(true);
            }}
          >
            Rename
          </button>
        )}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="stack">
      {rename.error && <p className="error">{mutationError(rename.error)}</p>}
      <label>
        Circle name
        <input required autoFocus maxLength={NAME_MAX} value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <div className="button-row">
        <button type="submit" disabled={rename.isPending || !name.trim()}>
          Save
        </button>
        <button type="button" className="secondary" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type MemberChange = { circleId: string; member: Member } & (
  | { kind: "role"; role: Member["role"] }
  | { kind: "sharing"; sharing: Member["sharing"] }
  | { kind: "sosReach"; sosReach: boolean }
  | { kind: "remove" }
);

function MemberList({
  circle,
  members,
  selfId,
  bubbles,
  nowMs,
  supervisions,
  onSupervise,
}: {
  circle: Circle;
  members: Member[];
  selfId: string;
  bubbles: ReturnType<typeof useSharingBubbles>;
  nowMs: number;
  /** What `GET /circles/{id}/supervisions` gave the caller (empty for plain members). */
  supervisions: SupervisedMember[];
  onSupervise: (userId: string) => void;
}) {
  const queryClient = useQueryClient();
  const units = useUnits();
  const covering = bubblesFor(bubbles.bubbles, circle.id, nowMs);
  const [bubbleFormOpen, setBubbleFormOpen] = useState(false);
  const navigate = useNavigate();
  // Member lists report effective sharing, so the per-circle toggle needs the global switch.
  const globalSharing = useMe().data?.sharing;
  const pausedEverywhere = globalSharing === "paused";
  // The caller's own supervision here: locked actions become requests.
  const pauseMode = actionMode("pause_circle", [circle], circle.id);
  const bubbleMode = actionMode("bubble_circle", [circle], circle.id);
  const leaveMode = actionMode("leave", [circle], circle.id);
  const myRequests = useMyRequests(circle.supervision !== null);
  const ask = useAsk();
  const [asking, setAsking] = useState<"pause_circle" | "leave" | null>(null);
  const [pauseHours, setPauseHours] = useState<PauseChoice>(null);
  const shownRequests = recentRequests(myRequests.data ?? [], ["pause_circle", "bubble_circle", "leave"], nowMs).filter(
    (request) => request.params.circle_id === circle.id,
  );
  const requested = (action: "pause_circle" | "bubble_circle" | "leave") =>
    shownRequests.some((request) => request.action === action && isPending(request, nowMs));
  const change = useMutation({
    mutationFn: async (c: MemberChange) => {
      const path = { circleId: c.circleId, userId: c.member.user_id };
      if (c.kind === "role") {
        unwrap(await api.PATCH("/circles/{circleId}/members/{userId}", { params: { path }, body: { role: c.role } }));
      } else if (c.kind === "sharing") {
        unwrap(
          await api.PATCH("/circles/{circleId}/members/{userId}", { params: { path }, body: { sharing: c.sharing } }),
        );
      } else if (c.kind === "sosReach") {
        unwrap(
          await api.PATCH("/circles/{circleId}/members/{userId}", {
            params: { path },
            body: { sos_reach: c.sosReach },
          }),
        );
      } else {
        const result = await api.DELETE("/circles/{circleId}/members/{userId}", { params: { path } });
        // Already gone (left or removed elsewhere) is what we wanted.
        if (!result.response.ok && result.response.status !== 404) unwrap(result);
      }
    },
    // A supervision started or tightened since the page loaded: show the Ask buttons.
    onError: async (error, c) => {
      if (error instanceof ApiError && error.code === "supervised")
        await queryClient.invalidateQueries({ queryKey: ["circle", c.circleId] });
    },
    onSuccess: async (_, c) => {
      if (c.kind === "remove" && c.member.user_id === selfId) {
        forgetCircle(selfId, c.circleId);
        queryClient.removeQueries({ queryKey: ["circle", c.circleId] });
        await queryClient.invalidateQueries({ queryKey: ["circles"] });
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["circle", c.circleId] }),
        queryClient.invalidateQueries({ queryKey: ["circles"] }),
        // Opting in to SOS reach while an SOS is open ends this circle's bubble.
        ...(c.kind === "sosReach" ? [queryClient.invalidateQueries({ queryKey: BUBBLES_KEY })] : []),
      ]);
    },
  });
  const left = () => void navigate("/circles", { replace: true });

  function remove(member: Member) {
    const self = member.user_id === selfId;
    const question = self
      ? `Leave ${circle.name}? Its members will no longer see your location, and you'll need a new invite to rejoin.${notice(leaveMode)}`
      : `Remove ${member.name} from ${circle.name}? They'll need a new invite to rejoin.`;
    if (!window.confirm(question)) return;
    change.mutate({ circleId: circle.id, kind: "remove", member }, self ? { onSuccess: left } : undefined);
  }

  function setRole(member: Member, role: Member["role"]) {
    const question =
      role === "admin"
        ? `Make ${member.name} an admin? Admins can invite and remove members, and delete the circle.`
        : member.user_id === selfId
          ? "Give up admin? You won't be able to manage invites or members any more."
          : null;
    if (question && !window.confirm(question)) return;
    change.mutate({ circleId: circle.id, kind: "role", member, role });
  }

  function setSharing(member: Member, sharing: Member["sharing"]) {
    const question = `Pause sharing with ${circle.name}? Its members won't see your location until you resume.${notice(pauseMode)}`;
    if (sharing === "paused" && !window.confirm(question)) return;
    change.mutate({ circleId: circle.id, kind: "sharing", member, sharing });
  }

  function setSosReach(member: Member, sosReach: boolean) {
    const question = `Stop sending your SOS alerts to ${circle.name}? Its members won't be alerted, and your SOS won't show them where you are while you pause sharing or share an approximate area. Your usual sharing with the circle doesn't change.`;
    if (!sosReach && !window.confirm(question)) return;
    change.mutate({ circleId: circle.id, kind: "sosReach", member, sosReach });
  }

  const busy = change.isPending ? change.variables.member.user_id : null;
  return (
    <>
      {change.error && (
        <p className="error" role="alert">
          {mutationError(change.error)}
        </p>
      )}
      {bubbles.error && (
        <p className="error" role="alert">
          {bubbles.error}
        </p>
      )}
      <ul className="circle-members">
        {sortMembers(members, selfId).map((member) => {
          const self = member.user_id === selfId;
          const actions = memberActions(member, selfId, circle.role, members);
          const supervision = supervisions.find((entry) => entry.user_id === member.user_id);
          const guarded = !self && supervision !== undefined && involves(supervision, selfId);
          const canSupervise =
            circle.role === "admin" &&
            !self &&
            member.role === "member" &&
            !member.supervised &&
            supervision === undefined &&
            circleControls(circle).length > 0;
          return (
            <li key={member.user_id} className="circle-member">
              <MemberAvatar
                userId={member.user_id}
                name={member.name}
                avatarUrl={member.avatar_small_url ?? null}
                isSelf={self}
              />
              <span className="circle-member-text">
                <span className="circle-member-name">
                  {member.name}
                  {self && " (you)"}
                  {member.supervised ? (
                    <span className="supervised-badge">Supervised</span>
                  ) : (
                    guarded &&
                    supervision?.offer && <span className="supervised-badge offered">Supervision offered</span>
                  )}
                </span>
                <span className="muted circle-member-meta">
                  {member.role === "admin" ? "Admin" : "Member"}
                  {member.sharing === "paused" &&
                    (self && pausedEverywhere ? " · Sharing paused everywhere" : " · Sharing paused")}
                  {self &&
                    member.sharing === "active" &&
                    covering.shown &&
                    ` · ${approximateText(covering.shown, nowMs, units)}`}
                  {self &&
                    circle.kind === "friends" &&
                    (circle.sos_reach ? " · Your SOS reaches this circle" : " · Your SOS doesn't reach this circle")}
                  {` · Joined ${new Date(member.joined_at).toLocaleDateString()}`}
                </span>
              </span>
              <span className="circle-member-actions">
                {self &&
                  globalSharing &&
                  (pausedEverywhere ? (
                    <Link to="/account" className="circle-member-link">
                      Resume in account settings
                    </Link>
                  ) : member.sharing === "active" && pauseMode === "ask" ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={requested("pause_circle") || asking === "pause_circle"}
                      onClick={() => setAsking("pause_circle")}
                      aria-label={`Ask a guardian to pause sharing with ${circle.name}`}
                    >
                      {requested("pause_circle") ? "Pause requested" : "Ask to pause"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busy !== null}
                      onClick={() => setSharing(member, member.sharing === "paused" ? "active" : "paused")}
                      aria-label={
                        member.sharing === "paused"
                          ? `Resume sharing with ${circle.name}`
                          : `Pause sharing with ${circle.name}`
                      }
                    >
                      {member.sharing === "paused" ? "Resume sharing" : "Pause sharing"}
                    </button>
                  ))}
                {self && member.sharing === "active" && covering.own && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={bubbles.busy}
                    onClick={() => covering.own && bubbles.end.mutate(covering.own)}
                    aria-label={
                      covering.all
                        ? `Turn off approximate sharing for ${circle.name} only`
                        : `Share your exact location with ${circle.name} again`
                    }
                  >
                    {/* An every-circle bubble still covers this circle after this one ends. */}
                    {covering.all ? "Turn off for this circle" : "Share exact location"}
                  </button>
                )}
                {self && member.sharing === "active" && covering.all && (
                  <Link to="/account" className="circle-member-link">
                    Approximate everywhere: change in account settings
                  </Link>
                )}
                {self && member.sharing === "active" && !covering.shown && !bubbleFormOpen && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={bubbles.busy || bubbles.loading || requested("bubble_circle")}
                    onClick={() => setBubbleFormOpen(true)}
                    aria-label={
                      bubbleMode === "ask"
                        ? `Ask a guardian to share an approximate area with ${circle.name}`
                        : `Share an approximate area with ${circle.name}`
                    }
                  >
                    {bubbleMode !== "ask"
                      ? "Share approximate area"
                      : requested("bubble_circle")
                        ? "Approximate area requested"
                        : "Ask to share approximate area"}
                  </button>
                )}
                {self && circle.kind === "friends" && (
                  <label className="circle-member-toggle">
                    <input
                      type="checkbox"
                      checked={
                        change.isPending && change.variables.kind === "sosReach"
                          ? change.variables.sosReach
                          : circle.sos_reach
                      }
                      disabled={busy !== null}
                      onChange={(event) => setSosReach(member, event.target.checked)}
                    />
                    Send my SOS here
                  </label>
                )}
                {canSupervise && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => onSupervise(member.user_id)}
                    aria-label={`Supervise ${member.name}`}
                  >
                    Supervise
                  </button>
                )}
                {guarded && (
                  <a href="#supervision" className="circle-member-link">
                    Supervision
                  </a>
                )}
                {actions.promote && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy !== null}
                    onClick={() => setRole(member, "admin")}
                    aria-label={`Make ${member.name} an admin`}
                  >
                    Make admin
                  </button>
                )}
                {actions.demote && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy !== null}
                    onClick={() => setRole(member, "member")}
                    aria-label={self ? "Step down as admin" : `Remove ${member.name} as admin`}
                  >
                    {self ? "Step down" : "Remove admin"}
                  </button>
                )}
                {actions.remove && (
                  <button
                    type="button"
                    className="secondary danger"
                    disabled={busy !== null}
                    onClick={() => remove(member)}
                    aria-label={`Remove ${member.name} from the circle`}
                  >
                    Remove
                  </button>
                )}
                {self && leaveMode === "ask" && (
                  <button
                    type="button"
                    className="secondary danger"
                    disabled={!actions.leave || requested("leave") || asking === "leave"}
                    title={actions.leave ? undefined : LAST_ADMIN}
                    onClick={() => setAsking("leave")}
                    aria-label={`Ask a guardian to let you leave ${circle.name}`}
                  >
                    {requested("leave") ? "Leave requested" : "Ask to leave"}
                  </button>
                )}
                {self && leaveMode !== "ask" && (
                  <button
                    type="button"
                    className="secondary danger"
                    disabled={busy !== null || !actions.leave}
                    title={actions.leave ? undefined : LAST_ADMIN}
                    onClick={() => remove(member)}
                    aria-label={`Leave ${circle.name}`}
                  >
                    Leave
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {bubbleFormOpen && !covering.shown && (
        <div className="stack">
          <p className="muted">
            {circle.name} will see an area around you instead of where you are. Your history, place arrivals and drives
            with this circle are left out while it's on.
            {bubbleMode === "ask" && " A guardian has to approve it first; it starts once they do."}
            {notice(bubbleMode)}
          </p>
          {ask.error && (
            <p className="error" role="alert">
              {askError(ask.error)}
            </p>
          )}
          <BubbleForm
            circleId={circle.id}
            busy={bubbles.start.isPending || ask.isPending}
            submitLabel={bubbleMode === "ask" ? "Ask to share approximate area" : undefined}
            onStart={(choice) =>
              bubbleMode === "ask"
                ? ask.mutate(
                    {
                      action: "bubble_circle",
                      params: bubbleParams(circle.id, choice.radiusM, choice.hours),
                    },
                    { onSuccess: () => setBubbleFormOpen(false) },
                  )
                : bubbles.start.mutate(choice, { onSuccess: () => setBubbleFormOpen(false) })
            }
            onCancel={() => {
              ask.reset();
              setBubbleFormOpen(false);
            }}
          />
        </div>
      )}
      {asking && (
        <AskForm
          key={asking}
          action={asking}
          params={
            asking === "pause_circle" ? { circle_id: circle.id, pause_hours: pauseHours } : { circle_id: circle.id }
          }
          submitLabel={asking === "pause_circle" ? "Ask to pause" : "Ask to leave"}
          intro={
            <p className="muted">
              {asking === "pause_circle"
                ? `Pausing sharing with ${circle.name} needs a guardian's approval. It pauses as soon as one approves.`
                : `Leaving ${circle.name} needs a guardian's approval. You leave as soon as one approves.`}
            </p>
          }
          onSent={() => setAsking(null)}
          onCancel={() => setAsking(null)}
        >
          {asking === "pause_circle" && (
            <label>
              Pause
              <select
                value={pauseHours ?? ""}
                onChange={(event) =>
                  setPauseHours(event.target.value === "" ? null : (Number(event.target.value) as PauseChoice))
                }
              >
                {PAUSE_CHOICES.map((hours) => (
                  <option key={hours ?? "open"} value={hours ?? ""}>
                    {pauseChoiceText(hours)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </AskForm>
      )}
      {shownRequests.map((request) => (
        <RequestState key={request.id} request={request} label={myRequestText(request)} />
      ))}
    </>
  );
}

function InvitesCard({
  circle,
  ended,
  members,
  selfId,
}: {
  circle: Circle;
  ended: boolean;
  members: readonly Member[];
  selfId: string;
}) {
  const circleId = circle.id;
  const full = isFull(circle);
  const queryClient = useQueryClient();
  const invites = useQuery({
    queryKey: ["circle", circleId, "invites"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/invites", { params: { path: { circleId } } })),
    refetchInterval: INVITES_POLL_MS,
  });
  const [ttlHours, setTtlHours] = useState<number>(DEFAULT_TTL_HOURS);
  const [maxUses, setMaxUses] = useState("");
  // A supervised invite's controls and guardians; null for an ordinary invite.
  const [supervision, setSupervision] = useState<SupervisionDraft | null>(null);
  const canSupervise = circleControls(circle).length > 0;
  const candidates = eligibleGuardians(members, null);
  // The invite whose QR code is showing; a new invite shows its own.
  const [qrFor, setQrFor] = useState<string | null>(null);
  const nowMs = useNow(TICK_MS);

  // Lost admin rights (or the circle): hide cached codes and refresh the circle's role, which unmounts this card.
  const denied = invites.error instanceof ApiError && (invites.error.status === 403 || invites.error.status === 404);
  useEffect(() => {
    if (denied) void queryClient.invalidateQueries({ queryKey: ["circle", circleId], exact: true });
  }, [denied, queryClient, circleId]);

  // Cache keys come from the mutation's own circle, never the circle on screen when it settles.
  const invitesKey = (id: string) => ["circle", id, "invites"];
  const create = useMutation({
    mutationFn: async (vars: { circleId: string; body: Schemas["InviteCreate"] }) =>
      unwrap(
        await api.POST("/circles/{circleId}/invites", {
          params: { path: { circleId: vars.circleId } },
          body: vars.body,
        }),
      ),
    onSuccess: async (invite) => {
      queryClient.setQueryData<Invite[]>(invitesKey(invite.circle_id), (prev) => [invite, ...(prev ?? [])]);
      await queryClient.invalidateQueries({ queryKey: invitesKey(invite.circle_id) });
    },
  });
  const revoke = useMutation({
    mutationFn: async (vars: { circleId: string; code: string }) => {
      const result = await api.DELETE("/circles/{circleId}/invites/{code}", { params: { path: vars } });
      // Already gone (revoked elsewhere, expired or used up) is what we wanted.
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSuccess: async (_, vars) => {
      queryClient.setQueryData<Invite[]>(invitesKey(vars.circleId), (prev) =>
        prev?.filter((i) => i.code !== vars.code),
      );
      await queryClient.invalidateQueries({ queryKey: invitesKey(vars.circleId) });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const uses = maxUses.trim() === "" ? null : Number(maxUses);
    const body = inviteCreateBody(ttlHours, uses, canSupervise ? supervision : null, circleControls(circle));
    create.mutate(
      { circleId, body },
      {
        onSuccess: (invite) => {
          setQrFor(invite.code);
          setSupervision(null);
        },
      },
    );
  }

  const active = (denied ? [] : (invites.data ?? [])).filter((invite) => Date.parse(invite.expires_at) > nowMs);
  const isEvent = circle.kind === "event";
  return (
    <section className="card stack">
      <h2>Invite people</h2>
      <p className="muted">
        {isEvent
          ? "Anyone with an invite link can join this event until it ends, or until the link expires or is used up. Members see each other's locations only while the event is on."
          : "Anyone with an invite link can join this circle and see members' locations until it expires or is used up."}
      </p>
      {ended ? (
        <p className="invite-full" role="status">
          The event has ended, so nobody can join any more.
        </p>
      ) : (
        full && (
          <p className="invite-full" role="status">
            <strong>{fullText(circle.kind)}</strong> ({memberCountText(circle)}). Invite links stop working until
            someone leaves.
          </p>
        )
      )}
      {!ended && (
        <form onSubmit={submit} className="invite-form">
          <label>
            Expires after
            <select value={ttlHours} onChange={(event) => setTtlHours(Number(event.target.value))}>
              {TTL_OPTIONS.map((option) => (
                <option key={option.hours} value={option.hours}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Maximum uses
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_USES_LIMIT}
              step={1}
              placeholder="Unlimited"
              disabled={supervision !== null}
              value={supervision ? "1" : maxUses}
              onChange={(event) => setMaxUses(event.target.value)}
            />
          </label>
          {canSupervise && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={supervision !== null}
                onChange={(event) =>
                  setSupervision(event.target.checked ? initialDraft(circleControls(circle), null, null, selfId) : null)
                }
              />
              Supervised invite
            </label>
          )}
          {supervision && (
            <div className="stack invite-supervision">
              <p className="muted">
                Whoever joins with this link is supervised as set here. It works once. Before joining they see the
                controls and guardians, and they can stop supervision on their own after a notice period.
              </p>
              <SupervisionFields
                available={circleControls(circle)}
                candidates={candidates}
                selfId={selfId}
                draft={supervision}
                onChange={setSupervision}
              />
            </div>
          )}
          <button
            type="submit"
            disabled={create.isPending || full || (supervision !== null && !draftReady(supervision, candidates))}
          >
            {supervision ? "Create supervised invite" : "Create invite link"}
          </button>
        </form>
      )}
      {create.error && <p className="error">{mutationError(create.error)}</p>}
      {revoke.error && <p className="error">{mutationError(revoke.error)}</p>}
      {invites.error && <p className="error">{mutationError(invites.error)}</p>}
      {invites.data && !denied && active.length === 0 && <p className="muted">No active invites.</p>}
      {active.length > 0 && (
        <ul className="invite-list">
          {active.map((invite) => (
            <InviteItem
              key={invite.code}
              invite={invite}
              nowMs={nowMs}
              qrShown={qrFor === invite.code}
              onToggleQr={() => setQrFor((prev) => (prev === invite.code ? null : invite.code))}
              revoking={revoke.isPending && revoke.variables.code === invite.code}
              onRevoke={() => {
                if (window.confirm(`Revoke invite ${formatInviteCode(invite.code)}? The link will stop working.`))
                  revoke.mutate({ circleId, code: invite.code });
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function InviteItem(props: {
  invite: Invite;
  nowMs: number;
  qrShown: boolean;
  onToggleQr: () => void;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const { invite } = props;
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied("ok");
    } catch {
      setCopied("failed");
    }
  }

  async function share() {
    try {
      await navigator.share({ title: "Join my ten20 circle", url: invite.url });
    } catch {
      // Cancelled, or the share target failed; the link is still on screen.
    }
  }

  return (
    <li className="invite-item">
      <div className="invite-item-main">
        <span className="invite-link">{invite.url}</span>
        <span className="muted invite-item-meta">
          Code <strong className="invite-code">{formatInviteCode(invite.code)}</strong> ·{" "}
          {expiryText(invite.expires_at, props.nowMs)} · {usesText(invite)}
          {invite.supervised && " · Supervised"}
        </span>
      </div>
      <div className="button-row">
        <button type="button" className="secondary" onClick={() => void copy()}>
          {copied === "ok" ? "Copied" : copied === "failed" ? "Copy failed" : "Copy link"}
        </button>
        {typeof navigator.share === "function" && (
          <button type="button" className="secondary" onClick={() => void share()}>
            Share
          </button>
        )}
        <button type="button" className="secondary" aria-expanded={props.qrShown} onClick={props.onToggleQr}>
          {props.qrShown ? "Hide QR code" : "QR code"}
        </button>
        <button type="button" className="secondary danger" disabled={props.revoking} onClick={props.onRevoke}>
          Revoke
        </button>
      </div>
      {props.qrShown && (
        <div className="invite-qr">
          <QrCode text={invite.url} label={`QR code for invite link ${invite.url}`} />
          <span className="muted">Scan with a phone camera to join.</span>
        </div>
      )}
    </li>
  );
}

function DeleteCircleCard({ circle }: { circle: Circle }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: async (circleId: string) => {
      const result = await api.DELETE("/circles/{circleId}", { params: { path: { circleId } } });
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSuccess: async (_, circleId) => {
      if (session) forgetCircle(session.userId, circleId);
      queryClient.removeQueries({ queryKey: ["circle", circleId] });
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });

  function confirmDelete() {
    const question = `Delete ${circle.name} for everyone? All members are removed and its places, invites and chat are deleted.`;
    if (!window.confirm(question)) return;
    remove.mutate(circle.id, { onSuccess: () => void navigate("/circles", { replace: true }) });
  }

  return (
    <section className="card stack">
      <h2>{circle.kind === "event" ? "Delete event" : "Delete circle"}</h2>
      <p className="muted">
        Removes every member, place and invite, and the chat. Members keep their own location history.
      </p>
      {remove.error && <p className="error">{mutationError(remove.error)}</p>}
      <button type="button" className="secondary danger" disabled={remove.isPending} onClick={confirmDelete}>
        Delete {circle.name}
      </button>
    </section>
  );
}

function KindCard({ circle }: { circle: Circle }) {
  const queryClient = useQueryClient();
  const to = circle.kind === "family" ? "friends" : "family";
  const change = useMutation({
    mutationFn: async (vars: { circleId: string; kind: "family" | "friends" }) =>
      unwrap(
        await api.PATCH("/circles/{circleId}", {
          params: { path: { circleId: vars.circleId } },
          body: { kind: vars.kind },
        }),
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["circle", updated.id], updated);
      // Switching to family ends the bubble of anyone whose open SOS now reaches it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["circles"] }),
        queryClient.invalidateQueries({ queryKey: ["circle", updated.id] }),
        queryClient.invalidateQueries({ queryKey: BUBBLES_KEY }),
      ]);
    },
  });

  function confirmSwitch() {
    if (!window.confirm(`Make ${circle.name} a ${to} circle?\n\n${kindSwitchText(to)}`)) return;
    change.mutate({ circleId: circle.id, kind: to });
  }

  return (
    <section className="card stack">
      <h2>Circle type</h2>
      <p className="muted">
        {circle.kind === "family"
          ? "A family circle: members share their exact location and every member's SOS reaches it."
          : "A friends circle: members join sharing an area of about 2 km, check-in alerts are off by default, and each member chooses whether their SOS reaches it."}
      </p>
      <p className="hint">{kindSwitchText(to)}</p>
      {change.error && <p className="error">{mutationError(change.error)}</p>}
      <button type="button" className="secondary" disabled={change.isPending} onClick={confirmSwitch}>
        Make it a {to} circle
      </button>
    </section>
  );
}

function EventCard({ circle, event, nowMs }: { circle: Circle; event: CircleEvent; nowMs: number }) {
  const queryClient = useQueryClient();
  const [viewerZone] = useState(localTimeZone);
  const [editing, setEditing] = useState(false);
  const status = eventStatus(event, nowMs);
  const members = useQuery({
    queryKey: ["circle", circle.id, "members"],
    queryFn: async () =>
      unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId: circle.id } } })),
  });
  const organiser = members.data?.find((member) => member.user_id === event.organiser_id)?.name;
  const update = useMutation({
    mutationFn: async (vars: { circleId: string; body: Schemas["CircleUpdate"] }) =>
      unwrap(
        await api.PATCH("/circles/{circleId}", { params: { path: { circleId: vars.circleId } }, body: vars.body }),
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["circle", updated.id], updated);
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });

  function save(draft: EventDraft) {
    const timesFixed = status === "ended";
    if (draftProblem(draft, Date.now(), timesFixed, event)) return;
    const body = eventUpdateBody(draft, circle.name, event, timesFixed);
    if (body === null) {
      setEditing(false);
      return;
    }
    update.mutate({ circleId: circle.id, body }, { onSuccess: () => setEditing(false) });
  }

  if (editing) {
    return (
      <section className="card stack">
        <h2>Edit event</h2>
        <EventForm
          initial={draftFromEvent(circle.name, event)}
          timesFixed={status === "ended"}
          original={event}
          submitLabel="Save"
          busy={update.isPending}
          error={update.error ? mutationError(update.error) : null}
          onSubmit={save}
          onCancel={() => {
            update.reset();
            setEditing(false);
          }}
        />
      </section>
    );
  }

  const point = event.meeting_point;
  return (
    <section className={`card stack event-card ${status}`}>
      <div className="event-card-head">
        <h2>{scheduleText(event, viewerZone)}</h2>
        <span className={`event-badge ${status}`}>{STATUS_LABELS[status]}</span>
      </div>
      <p className="event-countdown" aria-live="polite">
        {countdownText(event, nowMs)}
      </p>
      {organiser && <p className="muted">Organised by {organiser}</p>}
      {event.description && <p className="event-description">{event.description}</p>}
      {status === "upcoming" && (
        <p className="muted">
          Nobody's location is shared until the event starts. Until then you can chat and invite people.
        </p>
      )}
      {status === "live" && (
        <p>
          Members can see each other until the event ends.{" "}
          <Link to={`/?circle=${encodeURIComponent(circle.id)}`}>Open the live map</Link>
        </p>
      )}
      {status === "ended" && (
        <p className="muted">
          Locations are no longer shared. The chat closes on{" "}
          {new Date(event.chat_closes_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}.{" "}
          {deletionText(event, viewerZone)}, with its chat and places: save anything you want to keep.
        </p>
      )}
      {point && status !== "ended" && (
        <div className="stack event-meeting-point">
          <h3>Meeting point{point.label ? `: ${point.label}` : ""}</h3>
          <Suspense fallback={<div className="point-map" />}>
            <PointMap
              point={point}
              onPick={null}
              label={`Map of the meeting point${point.label ? `, ${point.label}` : ""}`}
            />
          </Suspense>
          <p>
            <a
              href={`https://www.openstreetmap.org/?mlat=${point.lat}&mlon=${point.lon}#map=17/${point.lat}/${point.lon}`}
              target="_blank"
              rel="noreferrer"
            >
              Open in a map
            </a>
          </p>
        </div>
      )}
      {circle.role === "admin" && (
        <p>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              update.reset();
              setEditing(true);
            }}
          >
            Edit event
          </button>
        </p>
      )}
    </section>
  );
}
